import { useClosestDimensionPoints } from './useClosestDimensionPoints.js';
import type { Point } from '../../core/types/point.js';
import type { DimensionXYPoint } from '../types/categorical-bar-chart-data.js';
import type { GroupMode, Layout } from '../types/categorical-bar-chart.js';
import { getClosestDistance } from '../utils/getClosestDistance.js';
import { isStacked, isVertical } from '../utils/layout.js';

/**
 * Retrieve the dimension X/Y point based on the
 * closest category considering the current mouse position, layer and group mode
 *
 * @param layout - layout mode of the chart
 * @param groupMode - group mode of the chart
 * @param mousePosition - mouse coordinate
 * @returns closest point and closest points of the mouse coordinate
 */
export const useClosestDimensionPoint = (
  layout: Layout,
  groupMode: GroupMode = 'stacked',
  mousePosition?: Point,
) => {
  const closestDimensionPoints = useClosestDimensionPoints(
    layout,
    groupMode,
    mousePosition,
  );

  const closestDimensionPoint = findClosestDimensionPoint(
    layout,
    mousePosition,
    closestDimensionPoints,
    groupMode,
  );

  return {
    closestDimensionPoint: closestDimensionPoint ?? closestDimensionPoints[0],
    closestDimensionPoints,
  };
};

const findClosestDimensionPoint = (
  layout: Layout,
  mousePosition: Point | undefined,
  closestDimensionPoints: DimensionXYPoint[],
  groupMode: GroupMode,
): DimensionXYPoint | undefined => {
  if (mousePosition && closestDimensionPoints.length > 0) {
    return closestDimensionPoints.reduce(
      (p1, p2) =>
        getClosestDistance(
          mousePosition,
          p1,
          p2,
          isVertical(layout),
          isStacked(groupMode),
        ),
      closestDimensionPoints[0],
    );
  }

  return undefined;
};
